import { Circle as CircleStyle, Fill, Stroke, Style, Text, Icon } from 'ol/style';
import mdiMapping from 'app/components/molecules/Map/EntityPin/entitiesPinMap.json';
import customIconSet from 'app/components/molecules/Map/EntityPin/customEntityPin.js';
import { getPriorityColor } from 'app/config/aboxConfig';
import theme from 'app/themes/theme.default';
import { LAYERS, PROCESSLAYERS, encodeSvgTobase64, DEFAULT_ZOOM_LEVEL } from 'app/utils/maps/layer/layerUtils';
import Chart from 'app/utils/maps/layer/ol-ext/Chart';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { isDefined } from 'app/utils/utils';
import { applyLayerZIndex } from 'app/config/layerZIndexConfig';


// Style caching system to prevent expensive style recreation
const styleCache = new Map();
const maxCacheSize = 5000;

export const stringDivider = (str, widthInPixels = 100, spaceReplacer, font) => {
    if (!str?.length) return '';
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    let lineWidth = 0;
    let result = '';
    for (let i = 0; i < str.length; i++) {
        const char = str[i];
        const charWidth = context.measureText(char).width;
        if (lineWidth + charWidth <= widthInPixels) {
            result += char;
            lineWidth += charWidth;
        } else {
            result += spaceReplacer + char;
            lineWidth = charWidth;
        }
    }
    return result;
};

const getFeatureId = (feature) => {
    if (feature.getId()) {
        return feature.getId();
    }
    
    // Check the nested structure
    if (feature?.values_?.features?.[0]?.id) {
        return feature.values_.features[0].id;
    }
    
    // Fallback to feature ID from attributes
    const attributes = feature.get('attributes');
    if (attributes?.id) {
        return attributes.id;
    }
    
    // Last resort: generate a hash from feature properties
    const featureData = JSON.stringify({
        geometry: feature.getGeometry()?.getCoordinates(),
        attributes: feature.get('attributes'),
        values: feature.values_
    });
    return `hash_${featureData.length}_${featureData.charCodeAt(0)}`;
};

const generateFeatureStyleKey = (feature, params) => {
    const featureId = getFeatureId(feature);
    const {
        selected = false,
        progress = 0,
        counter = 0,
        layerType = '',
        layerZIndex = 0,
        related = false,
        showCounters = false,
        iconName = '',
        zoomLevel = 0,
        mapTarget = '',
        hasSvg = false,
        hasGraphic = false,
        iconColor = '',
        priority = 0,
        styleColor = ''
    } = params;
    
    return `feature_${featureId}_${selected ? 1 : 0}_${progress}_${counter}_${layerType}_${layerZIndex}_${related ? 1 : 0}_${showCounters ? 1 : 0}_${iconName}_${Math.floor(zoomLevel)}_${mapTarget}_${hasSvg ? 1 : 0}_${hasGraphic ? 1 : 0}_${iconColor}_${priority}_${styleColor}`;
};

const generateClusterKey = (featureCount, selected, layerZIndex) => {
    return `cluster:${featureCount}:${selected ? 1 : 0}:${layerZIndex || 0}`;
};

const clearCacheIfNeeded = () => {
    if (styleCache.size > maxCacheSize) {
        // Clear 40% of cache when limit reached (more aggressive cleanup)
        const keysToRemove = Array.from(styleCache.keys()).slice(0, Math.floor(maxCacheSize * 0.4));
        keysToRemove.forEach(key => styleCache.delete(key));
    }
};

export const clearStyleCache = () => {
    styleCache.clear();
};

// Clear cache for specific feature
export const clearFeatureStyleCache = (feature) => {
    const featureId = getFeatureId(feature);
    const keysToRemove = Array.from(styleCache.keys()).filter(key => 
        key.includes(`feature_${featureId}_`)
    );
    keysToRemove.forEach(key => styleCache.delete(key));
};

// Clear cache for multiple features
export const clearFeaturesStyleCache = (features) => {
    features.forEach(feature => clearFeatureStyleCache(feature));
};

export const getStyleCacheSize = () => styleCache.size;

const getCachedStyle = (key) => {
    return styleCache.get(key);
};

const setCachedStyle = (key, style) => {
    clearCacheIfNeeded();
    styleCache.set(key, style);
    return style;
};

const clusterColorCache = new Map([
    [5, [72, 244, 66, 1]],
    [15, [209, 244, 66, 1]],
    [30, [244, 241, 66, 1]],
    [50, [244, 178, 65, 1]],
    [100, [244, 91, 65, 1]]
]);

export const getClusterStyleParams = (featureCount, selected) => {
    const cacheKey = `clusterParams:${featureCount}:${selected ? 1 : 0}`;
    const cached = getCachedStyle(cacheKey);
    if (cached) return cached;
    
    const colorKey = Object.keys(Object.fromEntries(clusterColorCache)).find(key => featureCount < Number(key)) || '100';
    const params = {
        color: clusterColorCache.get(Number(colorKey)),
        strokeColor: selected ? '#000' : '#fff',
        strokeWidth: selected ? 2 : 1,
        radius: 16 + featureCount / 400,
        fontSize: 13 + featureCount / 1000,
    };
    
    return setCachedStyle(cacheKey, params);
};

/**
 * Creates cluster style with proper z-index and caching
 * @param {Array} features - Array of clustered features
 * @param {boolean} selected - Whether cluster is selected
 * @param {number} layerZIndex - Base z-index of the parent layer
 * @returns {Style} OpenLayers style object
 */
export const clusterStyle = (features, selected = false, layerZIndex = null) => {
    const featureCount = features.length;
    const cacheKey = generateClusterKey(featureCount, selected, layerZIndex);
    
    // Check cache first
    const cachedStyle = getCachedStyle(cacheKey);
    if (cachedStyle) return cachedStyle;
    
    const { color, strokeColor, strokeWidth, radius, fontSize } = getClusterStyleParams(featureCount, selected);
    
    try {
        const style = new Style({
            image: new CircleStyle({
                radius: radius,
                stroke: new Stroke({
                    color: strokeColor,
                    width: strokeWidth,
                }),
                fill: new Fill({
                    color: color,
                }),
            }),
            text: new Text({
                text: featureCount.toString(),
                font: fontSize + 'px sans-serif',
                fill: new Fill({
                    color: '#fff',
                }),
                stroke: new Stroke({
                    color: 'rgb(0, 0, 0)',
                    width: 3,
                }),
            }),
        });

        applyLayerZIndex(layerZIndex, 'entity', style);

        return setCachedStyle(cacheKey, style);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error in implementing cluster style:', error);  
        return null;
    }
};



/**
 * Main entity pin styling function called by various layer types
 * @param {Object} params - Style parameters
 * @param {Feature} params.feature - OpenLayers feature
 * @param {Map} params.map - OpenLayers map
 * @param {boolean} [params.selected=false] - Whether feature is selected
 * @param {number} [params.layerZIndex=null] - Base z-index of the parent layer
 * @returns {Style|Array<Style>} OpenLayers style(s)
 */
export const entityPinStyle = ({ feature, map, selected = false, layerZIndex = null }) => {
    const features = feature.get('features');
    if (features && features.length > 1) {
        return clusterStyle(features, selected, layerZIndex);
    } else if (features && features.length === 1) {
        return singleFeatureStyle({ feature: features[0], map, selected, layerZIndex });
    }
    return singleFeatureStyle({ feature, map, selected, layerZIndex });
};



/**
 * Simplified entity pin styling function for basic use cases
 * @param {Feature} feature - OpenLayers feature
 * @param {Map} map - OpenLayers map
 * @param {number} layerZIndex - Base z-index of the parent layer
 * @param {Object} options - Optional styling options
 * @returns {Style|Array<Style>} OpenLayers style(s)
 */
export const entityPinStyleSimple = (feature, map, layerZIndex = null, options = {}) => {
    const {
        selected = false,
        progress = null,
        counter = null,
        layerType = null,
        progressDefaultVal = null,
        counterDefaultVal = null,
        is3DEnabled = null,
        style = null,
        progressConstraints = null,
        showCounters = null
    } = options;

    return singleFeatureStyle({
        feature,
        map,
        selected,
        progress,
        counter,
        layerType,
        progressDefaultVal,
        counterDefaultVal,
        is3DEnabled,
        style,
        progressConstraints,
        showCounters,
        layerZIndex
    });
};

/**
 * Enhanced single feature styling with z-index support
 * @param {Object} params - Style parameters
 * @param {Feature} params.feature - OpenLayers feature
 * @param {Map} params.map - OpenLayers map
 * @param {boolean} [params.selected=false] - Whether feature is selected
 * @param {string} [params.progress] - Progress attribute name
 * @param {string} [params.counter] - Counter attribute name
 * @param {string} [params.layerType] - Type of layer
 * @param {number} [params.progressDefaultVal] - Default progress value
 * @param {number} [params.counterDefaultVal] - Default counter value
 * @param {boolean} [params.is3DEnabled] - Whether 3D is enabled
 * @param {Object} [params.style] - Style configuration
 * @param {Object} [params.progressConstraints] - Progress constraints
 * @param {boolean} [params.showCounters] - Whether to show counters
 * @param {number} [params.layerZIndex=null] - Base z-index of the parent layer
 * @returns {Style|Array<Style>} OpenLayers style(s)
 */
export const singleFeatureStyle = ({
    feature,
    map,
    selected = false,
    progress,
    counter,
    layerType,
    progressDefaultVal,
    counterDefaultVal,
    is3DEnabled,
    style,
    progressConstraints,
    showCounters,
    layerZIndex = null
}) => {
    try {
        const featureType = feature.getGeometry().getType();
        const attributes = feature.get('attributes') ? feature.get('attributes') : feature?.values_?.features?.[0].values_.attributes;
        const { relationCounter } = feature?.values_?.features?.[0]?.values_ || {};
        const { iconName, iconColor, graphic, variables, size, svg, svgGraphic, svgTransformation, classAbout, related, primaryClass } = attributes || {};
        const { entitySvg, entityGraphic } = primaryClass || {};
        const zoomLevel = primaryClass?.entitySvgVisibleZoom || 4;
        const uploadedSvg = svg || entitySvg;
        const galleryGraphicSvg = svgGraphic || entityGraphic;
        const mapTarget = map?.values_?.target || 'map';
        const currentZoom = map?.getView()?.getZoom() || 0;
        const { _url } = graphic || {};
        
        // Determine color logic before cache key generation
        let color = style?.pinStyle === 'cluster' ? iconColor || '#00BCD4' : style?.['valFillColour'] || iconColor || '#00BCD4';
        if (variables) {
            color = theme.priorityGradients[getPriorityColor(variables.priority || 3)][0];
        }
        
        const cacheKey = generateFeatureStyleKey(feature, {
            selected,
            progress: progress || 0,
            counter: counter || 0,
            layerType: layerType || '',
            layerZIndex: layerZIndex || 0,
            related: false,
            showCounters: showCounters || false,
            iconName: iconName || '',
            zoomLevel: currentZoom,
            mapTarget: mapTarget,
            hasSvg: !!(svg || svgGraphic || uploadedSvg || galleryGraphicSvg),
            hasGraphic: !!_url,
            iconColor: iconColor || '',
            priority: variables?.priority || 0,
            styleColor: style?.['valFillColour'] || ''
        });
        
        // Check cache first
        const cachedStyle = getCachedStyle(cacheKey);
        if (cachedStyle) return cachedStyle;
        
        let resultStyles = null;
        
        if (!is3DEnabled && (featureType === 'Point' || featureType === 'MultiPoint')) {
            const { _url } = graphic || {};
            const name = style?.pinStyle === 'cluster' ? iconName || 'map-marker' : style?.['iconName'] || iconName || 'map-marker';
            //default diameter is 32 and radius is 16.
            const pointSize = style?.pinStyle === 'cluster' ? 16 : style?.['valPointSize'] / 2 || 16;
            const mapTarget = map?.values_?.target || 'map';
            if (_url && map.getView().getZoom() > DEFAULT_ZOOM_LEVEL) {
                resultStyles = getWfsStyle(_url, 100, map, layerZIndex);
            } else if ((svg || svgGraphic) && (mapTarget === 'aboutMap' || mapTarget === 'svgMap')) {
                resultStyles = createSvgStyle(svg, entitySvg, svgGraphic, entityGraphic, svgTransformation, map, classAbout, mapTarget, layerZIndex);
            } else if ((uploadedSvg || galleryGraphicSvg) && mapTarget === 'map' && map?.getView().getZoom() > zoomLevel) {
                resultStyles = createSvgStyle(svg, entitySvg, svgGraphic, entityGraphic, svgTransformation, map, classAbout, mapTarget, layerZIndex);
            } else {
                resultStyles = createMaterialIcon(
                    name,
                    color,
                    selected,
                    size,
                    feature,
                    progress,
                    counter,
                    layerType,
                    related || null,
                    progressDefaultVal,
                    counterDefaultVal,
                    pointSize,
                    progressConstraints,
                    relationCounter,
                    showCounters,
                    layerZIndex
                );
            }
        } else if (featureType === 'GeometryCollection') {
            resultStyles = setGeomCollectionStyle(feature, layerZIndex);
        } else if (featureType === 'LineString' || featureType === 'MultiLineString') {
            const properties = feature.get('properties');
            const { lineColor, lineWidth, lineDashed } = properties || {};
            
            const lineStyle = lineDashed ? 
                new Style({
                    stroke: new Stroke({
                        color: lineColor || 'blue',
                        width: lineWidth || 4,
                        lineDash: [40, 40],
                    }),
                }) :
                new Style({
                    stroke: new Stroke({
                        color: lineColor || 'blue',
                        width: lineWidth || 4,
                    }),
                });

            applyLayerZIndex(layerZIndex, 'related_sub_entity', lineStyle);
            resultStyles = lineStyle;
        } else if (featureType === 'Polygon' || featureType === 'MultiPolygon') {
            const polygonStyle = new Style({
                stroke: new Stroke({
                    color: iconColor || '#00BCD4',
                    width: 3,
                }),
                fill: new Fill({
                    color: iconColor || '#00BCD4',
                }),
            });

            applyLayerZIndex(layerZIndex, 'entity', polygonStyle);
            resultStyles = polygonStyle;
        }
        
        // Cache the result using feature ID
        return setCachedStyle(cacheKey, resultStyles);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error in implementing feature style:', error);  
        return null;
    }
};

export const createSvgStyle = (svg, entitySvg, svgGraphic, entityGraphic, svgTransformation, map, classAbout, mapTarget, layerZIndex = null) => {
    const entityLocationSvg = svg || svgGraphic;
    const classSvg = entitySvg || entityGraphic;
    const finalSvg = entityLocationSvg || classSvg;
    
    if (!finalSvg) return null;
    
    let src = null;

    if (finalSvg?.id) {
        src = getAttachmentUrl(finalSvg.id, 'graphic');
    } else if (finalSvg) {
        src = encodeSvgTobase64(finalSvg);
    }

    if (!src) return null;

    const imgSize = [100, 100];
    const requiredImgSize = mapTarget === 'aboutMap' ? [64, 64] : [128, 128];
    const scale = [requiredImgSize[0] / imgSize[0], requiredImgSize[1] / imgSize[1]];
    const img = getImageCanvas(src, 100, map);
    const { width, height } = img;

    const svgStyle = new Style({
        image: new Icon({
            img: img,
            scale,
            imgSize: [width, height],
        }),
    });

    applyLayerZIndex(layerZIndex, 'entity', svgStyle);

    return svgStyle;
};

export const createMaterialIcon = (
    name,
    color,
    selected,
    relatedSize = 16,
    feature,
    layerProgress,
    layerCounter,
    layerType,
    related,
    progressDefaultVal,
    counterDefaultVal,
    pointSize,
    progressConstraints,
    relationCounter,
    showCounters,
    layerZIndex = null
) => {
    const taskProgress = feature?.values_?.attributes?.primary?.progress || feature?.values_?.features?.[0]?.values_?.attributes?.primary?.progress;
    const entityProgress =
        feature?.values_?.attributes?.primary?.[layerProgress] || feature?.values_?.features?.[0]?.values_?.attributes?.primary?.[layerProgress];
    const currentProgress = taskProgress || entityProgress || progressDefaultVal || 0;
    const minProgress = progressConstraints?.min;
    const maxProgress = progressConstraints?.max;
    let progress = 0;
    if (taskProgress > 0) progress = taskProgress;
    else if (isDefined(minProgress) && isDefined(maxProgress)) {
        if (minProgress !== maxProgress && currentProgress > minProgress) {
            progress = Math.abs(((currentProgress - minProgress) / (maxProgress - minProgress)) * 100);
        }
    } else {
        progress = progressDefaultVal;
    }

    const counter =
        feature?.values_?.attributes?.primary?.[layerCounter] ||
        feature?.values_?.features?.[0]?.values_?.attributes?.primary?.[layerCounter] ||
        counterDefaultVal ||
        0;

    const cacheKey = generateFeatureStyleKey(feature, {
        selected,
        progress: progress || currentProgress,
        counter: counter,
        layerType,
        layerZIndex,
        related,
        showCounters: showCounters || counter > 0 || (relationCounter || 0) > 0,
        iconName: name || '',
        zoomLevel: 0,
        mapTarget: '',
        hasSvg: false,
        hasGraphic: false,
        iconColor: color || '',
        priority: 0,
        styleColor: ''
    });
    
    // Check cache first
    const cachedStyle = getCachedStyle(cacheKey);
    if (cachedStyle) return cachedStyle;

    const size = relatedSize / 2;
    const markerPoint = mdiMapping?.[name] || customIconSet?.[name] || mdiMapping['map-marker'];
    const font = `normal ${pointSize * 2 * 0.75}px affectli, "Material Design Icons"`;

    try {
        // Create text styles with caching consideration
        const fontSize = 11 < pointSize ? new Text({
            font: font,
            // Fix syntax error: use function call instead of assignment
            text: 'drawing-layer' === layerType ? stringDivider(layerType, 60, '\n', font) : String.fromCodePoint(markerPoint),
            fill: new Fill({ color: 'white' }),
        }) : null;

        const relatedFontSize = 11 < size ? new Text({
            font: `normal ${size * 2 * 0.75}px affectli, "Material Design Icons"`,
            text: String.fromCodePoint(markerPoint),
            fill: new Fill({ color: 'white' }),
        }) : null;

        // Create base styles
        const defaultStyle = new Style({
            image: new CircleStyle({
                radius: pointSize || size,
                fill: new Fill({ color }),
                stroke: new Stroke({
                    color: 'white',
                    width: 0,
                }),
            }),
            text: fontSize,
        });

        const relatedDefaultStyle = new Style({
            image: new CircleStyle({
                radius: size,
                fill: new Fill({ color }),
                stroke: new Stroke({
                    color: 'white',
                    width: 0,
                }),
            }),
            text: relatedFontSize,
        });
        
        const numberCounterStyle = new Style({
            image: new CircleStyle({
                radius: counter || (relationCounter || 0) > 0 ? 10 : 0,
                fill: new Fill({ color }),
                displacement: [21, 21],
            }),
            text: new Text({
                font: 'normal 14px',
                textAlign: 'center',
                text: relationCounter ? `${relationCounter}` : `${counter > 99 ? '99+' : counter > 0 ? counter.toString() : ''}`,
                offsetY: -21,
                offsetX: counter > 99 ? 22 : 21,
                fill: new Fill({ color: 'white' }),
            }),
        });
        
        const progressStyle = new Style({
            image: new Chart({
                type: 'donut',
                radius: 35,
                max: 5,
                colors: [color, 'grey'],
                data: [progress, 100 - progress],
                stroke: new Stroke({
                    color: '#fff',
                    width: 0,
                }),
            }),
        });

        // Apply z-index to all styles in batch
        if (layerZIndex !== null) {
            const subLayerType = related ? 'related_sub_entity' : 'entity';
            [defaultStyle, relatedDefaultStyle, numberCounterStyle, progressStyle].forEach(style => {
                applyLayerZIndex(layerZIndex, subLayerType, style);
            });
        }

        // Determine which styles to return based on conditions
        let resultStyles;
        if (related) {
            resultStyles = [relatedDefaultStyle];
        } else if (selected) {
            resultStyles = [defaultStyle];
        } else if ((progress || currentProgress) && (showCounters || counter)) {
            resultStyles = [defaultStyle, progressStyle, numberCounterStyle];
        } else if (progress || currentProgress) {
            resultStyles = [defaultStyle, progressStyle];
        } else if (layerType === LAYERS.event || PROCESSLAYERS.includes(layerType)) {
            resultStyles = [defaultStyle];
        } else if (showCounters || counter) {
            resultStyles = [defaultStyle, numberCounterStyle];
        } else {
            resultStyles = [defaultStyle];
        }
        
        return setCachedStyle(cacheKey, resultStyles);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error in implementing feature style:', error);  
        return null;
    }
};

export const createBubble = radius => {
    return new Style({
        image: new CircleStyle({
            radius,
            fill: new Fill({ color: [0, 81, 255, 0.3] }),
        }),
    });
};

const getWfsStyle = (src, size, map, layerZIndex = null) => {
    const img = getImageCanvas(src, size, map);
    const style = new Style({
        image: new Icon({
            img: img,
            imgSize: [size, size],
        }),
    });

    applyLayerZIndex(layerZIndex, 'entity', style);

    return style;
};

export const getImageCanvas = (src, size, map) => {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('width', size);
    canvas.setAttribute('height', size);
    canvas.setAttribute('class', 'svgPins');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = function () {
        const { width, height } = img;
        const aspectRatio = width / height;
        let imgWidth = size;
        let imgHeight = size / aspectRatio;
        if (aspectRatio < 1) {
            imgWidth = size * aspectRatio;
            imgHeight = size;
        }
        const xOffset = (size - imgWidth) / 2;
        const yOffset = (size - imgHeight) / 2;
        ctx.drawImage(img, xOffset, yOffset, imgWidth, imgHeight);
        map.render();
    };
    img.src = src;
    return canvas;
};

const setGeomCollectionStyle = (feature, layerZIndex = null) => {
    const geometries = feature?.getGeometry()?.getGeometries();
    const properties = feature?.values_?.attributes;
    const { iconColor } = properties || {};
    
    const styles = geometries && geometries.map(geometry => {
        const type = geometry.getType();
        let style = null;
        
        if (type === 'Polygon' || type === 'MultiPolygon') {
            style = new Style({
                stroke: new Stroke({
                    color: iconColor || '#00BCD4',
                    width: 3,
                }),
                fill: new Fill({
                    color: iconColor || '#00BCD4',
                }),
            });
        } else if (type === 'LineString' || type === 'MultiLineString') {
            style = new Style({
                stroke: new Stroke({
                    color: iconColor || '#FF5722',
                    width: 2,
                }),
            });
        } else if (type === 'Point' || type === 'MultiPoint') {
            style = new Style({
                image: new CircleStyle({
                    radius: 10,
                    fill: new Fill({ color: iconColor || '#00BCD4' }),
                    stroke: new Stroke({
                        color: 'blue',
                        width: 0,
                    }),
                }),
            });
        }

        if(style && isDefined(layerZIndex)){
            applyLayerZIndex(layerZIndex, 'entity', style);
        }

        return style;
    }).filter(Boolean);

    return styles;
};